import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { CustomIcon } from '../../UI';
import PDF from '../../../assets/icons/all/PDF.png';
import './DocumentManager.scss';

const DocumentManager = ({
  documents,
  onUpload,
  onRemove,
  onGenerate,
  uploadAccept = '.pdf',
  uploadLabel = 'Upload PDF',
  generateLabel = 'Generate PDF',
  hideGenerate = false,
  variant = 'default',
  generateLoading = false,
}) => {
  const fileInputRef = useRef(null);
  const nameInputRef = useRef(null);
  const pendingNameRef = useRef('');

  const handleUploadClick = () => {
    if (variant === 'inlineName' && nameInputRef.current) {
      pendingNameRef.current = nameInputRef.current.value.trim();
    }

    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0 && typeof onUpload === 'function') {
      const providedName =
        variant === 'inlineName' && pendingNameRef.current
          ? pendingNameRef.current
          : undefined;
      const filesToUpload = variant === 'inlineName' ? files.slice(0, 1) : files;

      filesToUpload.forEach((file) => onUpload(file, providedName));

      if (variant === 'inlineName' && nameInputRef.current) {
        nameInputRef.current.value = '';
        pendingNameRef.current = '';
      }
    }
    if (event.target) {
      event.target.value = '';
    }
  };

  const showGenerate =
    variant !== 'inlineName' && !hideGenerate && typeof onGenerate === 'function';

  return (
    <div className="document-manager">
      {variant === 'inlineName' ? (
        <div className="document-inline-form">
          <label className="inline-label" htmlFor="document-name-input">
            Add File
          </label>
          <div className="inline-controls">
            <input
              id="document-name-input"
              ref={nameInputRef}
              className="inline-input"
              placeholder="Text"
              type="text"
            />
            <button type="button" className="inline-add-btn" onClick={handleUploadClick}>
              Add file
            </button>
          </div>
        </div>
      ) : (
        <div className="document-actions">
          {showGenerate && (
            <button
              type="button"
              className="generate-pdf-btn"
              onClick={onGenerate}
              disabled={generateLoading}
            >
              {generateLabel}
            </button>
          )}
          <button type="button" className="upload-pdf-btn" onClick={handleUploadClick}>
            {uploadLabel}
          </button>
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={uploadAccept}
        multiple={variant !== 'inlineName'}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {documents.length > 0 && (
        <div className="documents-list">
          {documents.map((document, index) => {
            const hasStatus = document.status !== undefined;
            const progress =
              hasStatus && document.size && document.size > 0 && document.uploadedSize !== undefined
                ? Math.min(100, Math.round((document.uploadedSize / document.size) * 100))
                : null;
            const key = document.id != null ? document.id : `doc-${index}-${document.name ?? ''}`;

            return (
              <div key={key} className="document-item">
                <div className="document-icon">
                  <img src={PDF} alt="PDF icon" />
                </div>

                <div className="document-info">
                  <div className="document-name">{document.name}</div>
                  {document.url && (
                    <a
                      className="document-link"
                      href={document.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View
                    </a>
                  )}
                  {hasStatus ? (
                    <div className="upload-status">
                      {document.uploadedSize !== undefined && document.size ? (
                        <span className="upload-text">
                          {Math.round(document.uploadedSize)} KB of {document.size} KB
                        </span>
                      ) : (
                        <span className="upload-text">{document.size} KB</span>
                      )}

                      {document.status === 'uploading' && (
                        <div className="upload-indicator">
                          <CustomIcon name="loader-01" size={16} color="primary" />
                          <span>Uploading...</span>
                        </div>
                      )}

                      {document.status === 'completed' && (
                        <div className="upload-complete">
                          <CustomIcon name="check-01" size={16} color="success" />
                          <span>Completed</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="upload-status">
                      <span className="upload-text">{document.size} KB</span>
                    </div>
                  )}

                  {hasStatus && progress !== null && (
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${progress}%` }} />
                    </div>
                  )}
                </div>

                {typeof onRemove === 'function' && (
                  <button
                    type="button"
                    className="remove-document"
                    onClick={() => onRemove(document.id)}
                  >
                    <CustomIcon name="close" size={16} color="black" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

DocumentManager.propTypes = {
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      size: PropTypes.number.isRequired,
      uploadedSize: PropTypes.number.isRequired,
      status: PropTypes.oneOf(['uploading', 'completed', 'error']),
    })
  ),
  onUpload: PropTypes.func,
  onRemove: PropTypes.func,
  onGenerate: PropTypes.func,
  uploadAccept: PropTypes.string,
  uploadLabel: PropTypes.string,
  generateLabel: PropTypes.string,
  hideGenerate: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'inlineName']),
  generateLoading: PropTypes.bool,
};

DocumentManager.defaultProps = {
  documents: [],
  onUpload: undefined,
  onRemove: undefined,
  onGenerate: undefined,
  uploadAccept: '.pdf',
  uploadLabel: 'Upload PDF',
  generateLabel: 'Generate PDF',
  hideGenerate: false,
  variant: 'default',
  generateLoading: false,
};

export default DocumentManager;

